import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Spinner,
  Badge,
  Checkbox,
  ProgressBar,
  makeStyles,
  tokens,
  Tooltip,
  Button,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  CheckmarkCircleFilled,
  ClockRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { getDrakeChecklistByClient, updateDrakeItemReceived } from '../services/api';
import { DrakeChecklist, DrakeChecklistItem } from '../types';
import { getCategoryColor, formatCurrency } from '../utils/documentMapping';

interface DocumentChecklistProps {
  clientId: string;
  taxYear: number;
  variant?: 'full' | 'compact' | 'summary';
  showProgress?: boolean;
  editable?: boolean;
  onChecklistLoaded?: (checklist: DrakeChecklist | null) => void;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  progress: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  progressText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '80px',
  },
  progressBar: {
    flex: 1,
    minWidth: '100px',
    maxWidth: '200px',
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '10px 12px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: '12px',
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  itemReceived: {
    backgroundColor: '#dff6dd',
    border: '1px solid #107c10',
  },
  itemCheckbox: {
    marginTop: '2px',
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  itemCategory: {
    fontSize: tokens.fontSizeBase100,
    padding: '2px 6px',
    borderRadius: '4px',
    color: 'white',
  },
  itemName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  itemDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '4px',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  itemAmount: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  itemStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  compactItem: {
    padding: '6px 10px',
    gap: '8px',
  },
  summaryContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
  },
  summaryIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  summaryText: {
    flex: 1,
  },
  summaryTitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  summarySubtitle: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px',
    color: tokens.colorNeutralForeground3,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '24px',
  },
});

export default function DocumentChecklist({
  clientId,
  taxYear,
  variant = 'full',
  showProgress = true,
  editable = true,
  onChecklistLoaded,
}: DocumentChecklistProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();

  // Fetch Drake checklist for this client
  const { data: checklist, isLoading } = useQuery({
    queryKey: ['drakeChecklist', clientId, taxYear],
    queryFn: async () => {
      const result = await getDrakeChecklistByClient(clientId, taxYear);
      onChecklistLoaded?.(result);
      return result;
    },
    enabled: !!clientId && !!taxYear,
  });

  // Mutation to update item received status
  const updateItemMutation = useMutation({
    mutationFn: async ({ itemIndex, received }: { itemIndex: number; received: boolean }) => {
      if (!checklist) throw new Error('No checklist');
      return updateDrakeItemReceived(checklist.id, taxYear, itemIndex, received);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drakeChecklist', clientId, taxYear] });
    },
  });

  // Calculate progress
  const { receivedCount, totalCount, progress } = useMemo(() => {
    if (!checklist?.checklistItems?.length) {
      return { receivedCount: 0, totalCount: 0, progress: 0 };
    }
    const total = checklist.checklistItems.length;
    const received = checklist.checklistItems.filter((item) => item.received).length;
    return {
      receivedCount: received,
      totalCount: total,
      progress: total > 0 ? received / total : 0,
    };
  }, [checklist]);

  const handleToggleReceived = (itemIndex: number, currentReceived: boolean) => {
    if (!editable) return;
    updateItemMutation.mutate({ itemIndex, received: !currentReceived });
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="small" label="Loading checklist..." />
      </div>
    );
  }

  if (!checklist || !checklist.checklistItems?.length) {
    if (variant === 'summary') {
      return (
        <div className={styles.summaryContainer}>
          <InfoRegular className={styles.summaryIcon} />
          <div className={styles.summaryText}>
            <div className={styles.summaryTitle}>No Document Checklist</div>
            <div className={styles.summarySubtitle}>
              Upload a Drake PDF to generate a personalized checklist
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={styles.emptyState}>
        <DocumentRegular style={{ fontSize: '32px', marginBottom: '8px' }} />
        <div>No document checklist available for this client.</div>
        <div style={{ fontSize: '12px', marginTop: '4px' }}>
          Upload a Drake PDF to generate a personalized document checklist.
        </div>
      </div>
    );
  }

  // Summary variant - just show progress
  if (variant === 'summary') {
    const progressColor =
      progress === 1
        ? tokens.colorPaletteGreenForeground1
        : progress > 0.5
        ? tokens.colorPaletteBlueForeground2
        : tokens.colorPaletteYellowForeground1;

    return (
      <div className={styles.summaryContainer}>
        {progress === 1 ? (
          <CheckmarkCircleFilled
            className={styles.summaryIcon}
            style={{ color: tokens.colorPaletteGreenForeground1 }}
          />
        ) : (
          <ClockRegular
            className={styles.summaryIcon}
            style={{ color: progressColor }}
          />
        )}
        <div className={styles.summaryText}>
          <div className={styles.summaryTitle}>
            {receivedCount} of {totalCount} Documents Received
          </div>
          <div className={styles.summarySubtitle}>
            {progress === 1
              ? 'All documents collected'
              : `${totalCount - receivedCount} documents pending`}
          </div>
        </div>
        <ProgressBar
          value={progress}
          thickness="large"
          style={{ width: '100px' }}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header with progress */}
      {showProgress && (
        <div className={styles.header}>
          <div className={styles.title}>
            <DocumentRegular />
            Document Checklist
          </div>
          <div className={styles.progress}>
            <span className={styles.progressText}>
              {receivedCount}/{totalCount} received
            </span>
            <ProgressBar
              value={progress}
              className={styles.progressBar}
            />
          </div>
        </div>
      )}

      {/* Checklist items */}
      <div className={styles.itemList}>
        {checklist.checklistItems.map((item: DrakeChecklistItem, index: number) => (
          <div
            key={index}
            className={`${styles.item} ${item.received ? styles.itemReceived : ''} ${
              variant === 'compact' ? styles.compactItem : ''
            }`}
          >
            {editable && (
              <Checkbox
                className={styles.itemCheckbox}
                checked={item.received}
                onChange={() => handleToggleReceived(index, item.received)}
                disabled={updateItemMutation.isPending}
              />
            )}
            <div className={styles.itemContent}>
              <div className={styles.itemHeader}>
                <Badge
                  className={styles.itemCategory}
                  style={{ backgroundColor: getCategoryColor(item.category) }}
                  size="small"
                >
                  {item.category}
                </Badge>
                <span className={styles.itemName}>
                  {item.payerName || item.documentType || 'Document'}
                </span>
                {item.received && (
                  <Tooltip content="Document received" relationship="label">
                    <CheckmarkCircleFilled
                      style={{ color: tokens.colorPaletteGreenForeground1 }}
                    />
                  </Tooltip>
                )}
              </div>
              {variant === 'full' && (
                <div className={styles.itemDetails}>
                  {item.priorYearAmount !== undefined && item.priorYearAmount > 0 && (
                    <span className={styles.itemAmount}>
                      Prior year: {formatCurrency(item.priorYearAmount)}
                    </span>
                  )}
                  {item.ein && <span>EIN: {item.ein}</span>}
                  {item.entityType && <span>{item.entityType}</span>}
                  {item.notes && (
                    <Tooltip content={item.notes} relationship="label">
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={<InfoRegular />}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
            {variant !== 'compact' && item.received && item.receivedAt && (
              <div className={styles.itemStatus}>
                <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                  {new Date(item.receivedAt).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
